import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef } from 'react';

// Default product images by category
const DEFAULT_IMAGES = {
  agricultural: 'https://images.unsplash.com/photo-1579113800032-c38bd7635818?w=400&h=400&fit=crop',
  textiles: 'https://images.unsplash.com/photo-1552710307-537199cd41c0?w=400&h=400&fit=crop',
  furniture: 'https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg?w=400&h=400&fit=crop',
  minerals: 'https://images.pexels.com/photos/5765830/pexels-photo-5765830.jpeg?w=400&h=400&fit=crop',
  oil_gas: 'https://images.pexels.com/photos/247763/pexels-photo-247763.jpeg?w=400&h=400&fit=crop',
  vehicles: 'https://images.pexels.com/photos/3729464/pexels-photo-3729464.jpeg?w=400&h=400&fit=crop',
  food_beverages: 'https://images.unsplash.com/photo-1606923829579-0cb981a83e2e?w=400&h=400&fit=crop',
  electronics: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=400&fit=crop',
  default: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=400&h=400&fit=crop'
};

const getProductImage = (product) => {
  if (product.images && product.images.length > 0 && product.images[0]) {
    return product.images[0];
  }
  return DEFAULT_IMAGES[product.category] || DEFAULT_IMAGES.default;
};

// Category Box Component (2x2 grid like Amazon)
const CategoryBox = ({ title, products, linkTo }) => {
  if (!products || products.length === 0) return null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <h3 className="font-bold text-lg text-gray-900 mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {products.slice(0, 4).filter(p => p && p.id).map((product, idx) => (
          <Link 
            key={product.id || idx} 
            to={`/products/${product.id}`}
            className="group"
          >
            <div className="aspect-square bg-gray-100 rounded overflow-hidden mb-1">
              <img 
                src={getProductImage(product)} 
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                onError={(e) => { e.target.src = DEFAULT_IMAGES.default; }}
              />
            </div>
            <p className="text-xs text-gray-600 truncate">{product.name?.split(' ').slice(0, 3).join(' ')}</p>
          </Link>
        ))}
      </div>
      <Link to={linkTo} className="text-sm text-[#007185] hover:text-[#C7511F] hover:underline">
        Shop all
      </Link>
    </div>
  );
};

// Horizontal Product Carousel
const ProductCarousel = ({ title, products }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  
  if (!products || products.length === 0) return null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-xl text-gray-900">{title}</h3>
        <div className="flex gap-2">
          <button 
            onClick={() => scroll('left')}
            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.filter(p => p && p.id).map((product, idx) => (
          <Link 
            key={product.id || idx}
            to={`/products/${product.id}`}
            className="flex-shrink-0 w-40 group"
          >
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2">
              <img 
                src={getProductImage(product)} 
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                onError={(e) => { e.target.src = DEFAULT_IMAGES.default; }}
              />
            </div>
            <p className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-[#C7511F]">
              {product.name}
            </p>
            <p className="text-lg font-bold text-gray-900 mt-1">
              ${product.price?.toLocaleString()}
              <span className="text-xs font-normal text-gray-500 ml-1">/{product.unit}</span>
            </p>
            <p className="text-xs text-gray-500">{product.country_of_origin}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const FeaturedProductsSection = ({ products }) => {
  if (!products || products.length === 0) return null;
  
  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    const cat = product.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});
  
  // Get products for each category
  const agricultural = groupedProducts['agricultural'] || [];
  const textiles = groupedProducts['textiles'] || [];
  const furniture = groupedProducts['furniture'] || [];
  const minerals = groupedProducts['minerals'] || [];
  const oilGas = groupedProducts['oil_gas'] || [];
  const vehicles = groupedProducts['vehicles'] || [];
  const foodBeverages = groupedProducts['food_beverages'] || [];

  return (
    <section className="py-6 bg-[#E3E6E6]" data-testid="featured-products-section">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Category Boxes Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <CategoryBox 
            title="Farm Produce"
            products={agricultural}
            linkTo="/products?category=agricultural"
          />
          <CategoryBox 
            title="African Textiles & Fashion"
            products={textiles}
            linkTo="/products?category=textiles"
          />
          <CategoryBox 
            title="Furniture & Crafts"
            products={furniture}
            linkTo="/products?category=furniture"
          />
          <CategoryBox 
            title="Minerals & Metals"
            products={minerals}
            linkTo="/products?category=minerals"
          />
        </div>
        
        {/* Second Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <CategoryBox 
            title="Oil & Gas"
            products={oilGas}
            linkTo="/products?category=oil_gas"
          />
          <CategoryBox 
            title="Vehicles"
            products={vehicles}
            linkTo="/products?category=vehicles"
          />
          <CategoryBox 
            title="Food & Beverages"
            products={foodBeverages}
            linkTo="/products?category=food_beverages"
          />
          {/* Browse by Country */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="font-bold text-lg text-gray-900 mb-3">Browse by Country</h3>
            <div className="grid grid-cols-2 gap-2">
              {['Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Ethiopia'].map(country => (
                <Link 
                  key={country}
                  to={`/products?country=${country}`}
                  className="text-sm text-[#007185] hover:text-[#C7511F] hover:underline"
                >
                  {country}
                </Link>
              ))}
            </div>
          </div>
        </div>
        
        {/* Horizontal Carousels */}
        <ProductCarousel 
          title="Best Sellers in Farm Produce"
          products={agricultural.slice(0, 15)}
        />
        
        <ProductCarousel 
          title="African Textiles & Fashion"
          products={textiles.slice(0, 15)}
        />
        
        <ProductCarousel 
          title="Food & Beverages"
          products={foodBeverages.slice(0, 15)}
        />
        
        {/* View All CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-8 bg-white p-6 rounded-lg shadow-sm"
        >
          <p className="text-gray-600 mb-4">
            Discover {products.length}+ products from verified African suppliers
          </p>
          <Link to="/products">
            <Button 
              size="lg"
              className="bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 px-8 py-3 rounded-full font-semibold shadow-sm"
              data-testid="explore-products-btn"
            >
              View All Products <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
